import React, { useState, useCallback } from "react";
import { degrees } from "../portfolio";
import {
  FiExternalLink,
  FiX,
  FiBookOpen,
  FiAward,
  FiCheckCircle,
} from "react-icons/fi";
import {
  playBookSelect,
  playBookOpen,
  playBookClose,
  playHover,
  playClick,
} from "../utils/soundEngine";
import "./AcademicManualsShowcase.css";

const ACADEMIC_CONFIGS = [
  {
    roman: "I",
    coverColor: "#0d1a2d",
    coverText: "#e0f2fe",
    accent: "#38bdf8",
    level: "POSTGRADUATE DEGREE",
  },
  {
    roman: "II",
    coverColor: "#1e131c",
    coverText: "#fce7f3",
    accent: "#f472b6",
    level: "UNDERGRADUATE DEGREE",
  },
];

function AcademicManualsShowcase() {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const isDetail = selectedIdx !== null;
  const activeDegree = isDetail ? degrees.degrees[selectedIdx] : null;
  const activeConfig = isDetail ? ACADEMIC_CONFIGS[selectedIdx] : null;

  const handleSelect = useCallback((idx) => {
    playBookSelect();
    playBookOpen();
    setSelectedIdx(idx);
  }, []);

  const handleClose = useCallback(() => {
    playBookClose();
    setSelectedIdx(null);
  }, []);

  return (
    <div className="academic-stage" data-mode={isDetail ? "detail" : "gallery"}>
      {/* Background Hero Watermark Word */}
      <h3 className="academic-hero-word">
        {isDetail ? activeDegree?.alt_name || "ACADEMY" : "ACADEMIA"}
      </h3>

      {/* 2-Book 3D Fan Gallery */}
      <div className="academic-gallery">
        {degrees.degrees.map((degree, idx) => {
          const config = ACADEMIC_CONFIGS[idx] || ACADEMIC_CONFIGS[0];
          const isSelected = selectedIdx === idx;

          const logoPath = (() => {
            try {
              return require(`../assests/images/${degree.logo_path}`);
            } catch {
              return null;
            }
          })();

          return (
            <button
              key={degree.title}
              data-index={idx}
              className={`academic-book-card ${isSelected ? "selected" : ""}`}
              style={{
                "--book-color": config.coverColor,
                "--book-text": config.coverText,
                "--book-accent": config.accent,
              }}
              onClick={() => handleSelect(idx)}
              onMouseEnter={() => playHover()}
              type="button"
              aria-label={`Open academic dossier for ${degree.title}`}
            >
              <div className="academic-book">
                <div className="academic-book-shadow" />
                <div className="academic-book-back" />
                <div className="academic-page-block" />

                {/* 4-Leaf 3D Page Fan */}
                <div className="academic-page-fan">
                  <i />
                  <i />
                  <i />
                  <i />
                </div>

                {/* Hardcover Front */}
                <div className="academic-front-cover">
                  <div className="academic-cover-content">
                    {/* University Badge Logo */}
                    {logoPath && (
                      <img
                        src={logoPath}
                        alt={`${degree.title} crest`}
                        className="academic-cover-crest"
                      />
                    )}

                    <span className="academic-cover-roman">
                      VOLUME {config.roman} · {config.level}
                    </span>

                    <div>
                      <h4 className="academic-cover-degree">
                        {degree.subtitle}
                      </h4>
                      <p className="academic-cover-uni">{degree.title}</p>
                    </div>

                    <span className="academic-cover-footer">
                      {degree.duration} · VERIFIED CREDENTIAL
                    </span>
                  </div>
                </div>

                {/* Hover Rosette Open Badge */}
                <div className="academic-open-badge">OPEN</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Floating Close Button */}
      {isDetail && (
        <button
          onClick={handleClose}
          className="academic-close-btn"
          type="button"
          aria-label="Return to degrees overview"
        >
          <FiX size={18} />
        </button>
      )}

      {/* Detail Academic Dossier Panel */}
      {isDetail && activeDegree && activeConfig && (
        <aside
          className="academic-detail-panel"
          aria-label="Academic manual details"
        >
          <div className="academic-detail-scroll">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full bg-surface/[0.08] text-[11px] font-mono text-fg uppercase font-bold">
                VOL. {activeConfig.roman} · {activeConfig.level}
              </span>
              <span className="px-2.5 py-0.5 rounded-full bg-surface/[0.04] text-[11px] font-mono text-fg-faint">
                {activeDegree.duration}
              </span>
            </div>

            <h3 className="academic-detail-title">{activeDegree.subtitle}</h3>
            <p className="academic-detail-uni">{activeDegree.title}</p>

            {/* Academic Highlights & Coursework */}
            <div className="academic-doc-section">
              <h4 className="academic-doc-label flex items-center gap-2">
                <FiAward size={13} />
                Honors & Academic Focus
              </h4>
              <ul className="academic-doc-steps">
                {activeDegree.descriptions.map((desc, dIdx) => (
                  <li key={dIdx}>
                    <span className="step-num">
                      {String(dIdx + 1).padStart(2, "0")}
                    </span>
                    <span>{desc}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Core Competencies */}
            <div className="academic-doc-section">
              <h4 className="academic-doc-label flex items-center gap-2">
                <FiCheckCircle size={13} />
                Discipline Mastery
              </h4>
              <div className="flex flex-wrap gap-1.5 pb-2">
                {(selectedIdx === 0
                  ? [
                      "Artificial Intelligence",
                      "Machine Learning",
                      "Cloud Computing",
                      "Distributed Systems",
                      "Spring & Django",
                    ]
                  : [
                      "Data Structures & Algorithms",
                      "Operating Systems",
                      "Database Management",
                      "Computer Networks",
                      "Object-Oriented Design",
                    ]
                ).map((skill) => (
                  <span
                    key={skill}
                    className="px-2.5 py-1 rounded-md bg-surface/[0.04] border border-surface/[0.08] text-[11px] font-mono text-fg-faint"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Action Dock */}
          <div className="academic-action-dock">
            {activeDegree.website_link && (
              <a
                href={activeDegree.website_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => playClick()}
                className="academic-pill-btn primary"
              >
                <FiBookOpen size={12} />
                University Portal
                <FiExternalLink size={13} />
              </a>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

export default React.memo(AcademicManualsShowcase);
